import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, TrendingUp } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const NouvellesReglementationsAnj2025 = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/news"
          className="inline-flex items-center text-primary-red hover:text-primary-red-dark mb-8 transition-colors"
        >
          <ArrowLeft className="mr-2" size={20} />
          Retour aux actualités
        </Link>

        <article className="prose prose-lg max-w-none">
          <div className="flex items-center text-sm text-gray-500 mb-4">
            <Calendar size={16} className="mr-2" />
            <span>25.11.2025</span>
            <span className="mx-2">•</span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-600">
              <TrendingUp size={14} className="mr-1" />
              Réglementation
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Nouvelles réglementations ANJ : ce qui change en 2025
          </h1>

          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p className="text-xl text-gray-600 font-medium">
              L'Autorité Nationale des Jeux annonce de nouvelles mesures pour renforcer la protection des joueurs 
              et améliorer la transparence des opérateurs. Ces évolutions impactent directement votre expérience 
              de paris en ligne en France.
            </p>

            <p>
              Le secteur des paris en ligne en France entre dans une nouvelle phase de régulation. L'ANJ, 
              après plusieurs mois de consultation avec les opérateurs, les associations de joueurs, et les 
              experts du secteur, dévoile un ensemble de mesures qui vont transformer l'environnement des 
              paris en ligne. Ces changements ne sont pas cosmétiques : ils touchent au cœur de la relation 
              entre opérateurs et parieurs, avec un objectif clair : mieux protéger les joueurs tout en 
              maintenant un marché dynamique et innovant.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Renforcement des outils de contrôle
            </h2>

            <p>
              La mesure la plus significative concerne les outils de contrôle disponibles pour les parieurs. 
              Désormais, tous les opérateurs agréés doivent proposer des limites de dépôt encore plus 
              flexibles et granulaires. Vous pourrez fixer des limites non seulement quotidiennes, hebdomadaires 
              et mensuelles, mais aussi par session de jeu. Cette évolution répond à une demande forte des 
              associations de joueurs et permet un contrôle plus fin de votre activité.
            </p>

            <p>
              Les opérateurs devront aussi proposer des rappels automatiques plus fréquents. Si vous jouez 
              pendant une durée déterminée, vous recevrez automatiquement une notification vous rappelant 
              le temps écoulé. Ces rappels ne sont pas intrusifs, mais ils vous permettent de prendre 
              conscience du temps passé et de faire une pause si nécessaire. L'objectif n'est pas de vous 
              empêcher de jouer, mais de vous donner les outils pour rester maître de votre activité.
            </p>

            <p>
              L'exclusion temporaire devient aussi plus accessible. Vous pourrez vous exclure pour des 
              périodes très courtes (24 heures, 48 heures, une semaine) directement depuis votre compte, 
              sans avoir à contacter le service client. Cette facilité d'accès encourage l'utilisation 
              de cet outil de protection, qui peut être crucial dans les moments où vous sentez que vous 
              perdez le contrôle.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Transparence accrue sur les bonus
            </h2>

            <p>
              Les bonus de bienvenue et les promotions font l'objet d'une réglementation plus stricte. 
              Les opérateurs devront désormais afficher clairement, avant même que vous n'acceptiez un bonus, 
              toutes les conditions : le multiplicateur de mise (wager), le délai pour compléter les conditions, 
              les sports et types de paris éligibles, les cotes minimums. Plus de surprises désagréables 
              après coup : tout est transparent dès le départ.
            </p>

            <p>
              Les conditions de mise ne pourront plus dépasser un certain seuil. L'ANJ impose un plafond 
              sur le wager maximum autorisé, ce qui garantit que les bonus restent utilisables dans des 
              délais raisonnables. Cette mesure protège les parieurs contre les offres alléchantes mais 
              impossibles à convertir en gains réels.
            </p>

            <p>
              Les opérateurs devront aussi afficher clairement la valeur réelle d'un bonus. Au lieu de 
              simplement dire "100€ de bonus", ils devront indiquer combien vous devrez réellement miser 
              pour convertir ce bonus, et quel est le gain potentiel estimé. Cette transparence vous permet 
              de faire des choix éclairés et d'éviter les déceptions.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Protection renforcée des données
            </h2>

            <p>
              La protection de vos données personnelles est renforcée. Les opérateurs devront obtenir votre 
              consentement explicite avant d'utiliser vos données à des fins marketing. Plus de cases 
              pré-cochées : vous choisissez consciemment si vous acceptez de recevoir des offres promotionnelles 
              ou des newsletters.
            </p>

            <p>
              Les opérateurs devront aussi vous informer clairement de l'utilisation de vos données pour 
              le calcul des cotes et la personnalisation de l'expérience. Cette transparence vous permet 
              de comprendre comment vos données sont utilisées et de faire des choix éclairés sur ce que 
              vous acceptez de partager.
            </p>

            <p>
              En cas de violation de données, les opérateurs devront vous informer dans les 72 heures, 
              et l'ANJ devra être notifiée immédiatement. Cette mesure accélère la réaction en cas d'incident 
              et vous permet de prendre rapidement les mesures nécessaires pour protéger vos comptes et 
              vos informations personnelles.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Amélioration de la résolution des litiges
            </h2>

            <p>
              Le processus de médiation est simplifié et accéléré. Vous pourrez désormais saisir le médiateur 
              directement depuis votre compte, sans avoir à remplir de formulaires complexes. Le délai 
              de réponse des opérateurs est aussi réduit : ils devront répondre à votre réclamation dans 
              les 48 heures, au lieu de 5 jours actuellement.
            </p>

            <p>
              Le médiateur des jeux en ligne voit ses pouvoirs renforcés. Il pourra imposer des solutions 
              plus rapidement et avec moins de formalités. Cette évolution accélère la résolution des 
              litiges et garantit que vos droits sont respectés sans traîner dans des procédures interminables.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Impact sur votre expérience de paris en ligne
            </h2>

            <p>
              Ces nouvelles réglementations améliorent directement votre expérience de paris en ligne en 
              France. Vous avez plus de contrôle sur votre activité, plus de transparence sur les offres, 
              et plus de protection en cas de problème. Ces mesures ne sont pas restrictives : elles vous 
              donnent les outils pour parier en toute sérénité.
            </p>

            <p>
              Les opérateurs ont jusqu'au 1er janvier 2026 pour mettre en conformité leurs plateformes 
              avec ces nouvelles règles. Certains ont déjà commencé à adapter leurs interfaces et leurs 
              processus. Vous verrez progressivement ces améliorations apparaître sur les sites que vous 
              utilisez.
            </p>

            <p>
              Ces évolutions confirment que la France reste un marché mature et bien régulé pour les paris 
              en ligne. L'ANJ continue de trouver l'équilibre entre protection des joueurs et dynamisme 
              du secteur. Pour vous, parieurs, c'est une garantie supplémentaire que vous pouvez parier 
              en toute confiance, avec des règles claires et des protections solides.
            </p>

            <div className="bg-primary-red/10 border-l-4 border-primary-red p-6 mt-12 rounded-r-lg">
              <p className="text-gray-800 leading-relaxed">
                <strong>En résumé :</strong> Les nouvelles réglementations de l'ANJ pour 2025 renforcent 
                la protection des parieurs, améliorent la transparence des opérateurs, et simplifient la 
                résolution des litiges. Ces évolutions sont positives pour tous les parieurs français, 
                qui bénéficient d'un environnement plus sûr et plus transparent. Restez informé de ces 
                changements et n'hésitez pas à utiliser les nouveaux outils de contrôle disponibles.
              </p>
            </div>
          </div>
        </article>

        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/news"
            data-discover="true"
            className="inline-flex items-center text-primary-red hover:text-primary-red-dark transition-colors"
          >
            <ArrowLeft className="mr-2" size={20} />
            Retour à la liste des actualités
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NouvellesReglementationsAnj2025;

